/* eslint-disable */
/**
 * Single source of truth for JSON-LD structured data.
 *
 * Written in CommonJS on purpose: it is consumed both by the React app
 * (src/lib/seo.js) and by the build-time crawler prerender script
 * (scripts/prerender-seo.js), so schema can never drift between what
 * JavaScript-capable browsers see and what non-JS crawlers see.
 *
 * RULES followed here:
 *  - Only facts that exist on the live site are described.
 *  - No invented address, phone, social accounts, awards, ratings,
 *    partnerships, medical credentials or fellowship affiliations.
 *  - Sponsor Me is described as an independent peer-connection platform,
 *    never as treatment, therapy, counseling or medical care.
 *  - Pricing offers are only emitted when real, purchasable plan data is
 *    supplied by the live billing configuration (never hard-coded here).
 *  - Exactly one JSON-LD script (an @graph) is emitted per page.
 */
const config = require("./seo-pages.json");
const faqContent = require("./faq-content.json");

const SITE_URL = config.site.url;
const ORG_ID = SITE_URL + "/#organization";
const WEBSITE_ID = SITE_URL + "/#website";
const SERVICE_ID = SITE_URL + "/#service";

const UNIT_CODE = { week: "WEE", month: "MON", year: "ANN" };

function absolute(pathOrUrl) {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + pathOrUrl;
}

function canonicalFor(path) {
  return SITE_URL + (path === "/" ? "/" : path);
}

/** Organization - factual identity only. */
function organizationSchema() {
  const org = config.organization;
  const node = {
    "@type": "Organization",
    "@id": ORG_ID,
    name: config.site.name,
    url: SITE_URL + "/",
    description: org.description,
    logo: {
      "@type": "ImageObject",
      url: absolute(config.site.logo),
      width: config.site.logoWidth,
      height: config.site.logoHeight,
    },
    image: absolute(config.site.image),
    disambiguatingDescription: org.independenceDisclaimer,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: SITE_URL + "/contact",
    },
  };
  // sameAs ties this site to the company's other public profiles. It is
  // emitted ONLY when real URLs are listed in seo-pages.json -> site.sameAs.
  // An empty list means no verified Sponsor Me profile exists yet; a fake URL
  // here would be a false identity claim, so the key is simply omitted.
  //
  // NOTE: written as a conditional assignment rather than an object spread on
  // purpose. This file must stay pure CommonJS (it is `require`d by
  // scripts/prerender-seo.js); a spread makes Babel inject an ESM runtime
  // helper import, which turns the module into ESM for webpack and breaks
  // every `import { ... } from "./structured-data"` in the production build.
  if (Array.isArray(config.site.sameAs) && config.site.sameAs.length) {
    const verified = config.site.sameAs.filter(function (u) {
      return /^https?:\/\//.test(u);
    });
    if (verified.length) node.sameAs = verified;
  }
  if (org.founderName) {
    node.founder = {
      "@type": "Person",
      name: org.founderName,
      jobTitle: org.founderRole,
      description: org.founderDescription,
    };
    // The founder's own public profiles belong to the PERSON, not to the
    // company. They are listed here as Person.sameAs and deliberately NOT as
    // Organization.sameAs: no official Sponsor Me business accounts exist yet,
    // and claiming a personal account as the company's identity would be
    // inaccurate. Written as an assignment, never an object spread - see the
    // note above about keeping this file pure CommonJS.
    if (Array.isArray(org.founderLinks) && org.founderLinks.length) {
      const founderProfiles = [];
      for (let i = 0; i < org.founderLinks.length; i += 1) {
        const href = org.founderLinks[i] && org.founderLinks[i].href;
        if (href && /^https?:\/\//.test(href)) founderProfiles.push(href);
      }
      if (founderProfiles.length) node.founder.sameAs = founderProfiles;
    }
  }
  return node;
}

/** WebSite - with the directory search action that actually works. */
function websiteSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL + "/",
    name: config.site.name,
    description: config.organization.description,
    inLanguage: "en-US",
    publisher: { "@id": ORG_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: SITE_URL + "/sponsors?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** Turn live billing plans into schema.org Offers. */
function offersFromPlans(plans) {
  if (!Array.isArray(plans) || plans.length === 0) return null;
  const offers = [];
  for (const plan of plans) {
    if (!plan || typeof plan.amount !== "number" || !plan.interval) continue;
    const price = (plan.amount / 100).toFixed(2);
    offers.push({
      "@type": "Offer",
      name: plan.name,
      description: "Unlimited messaging on Sponsor Me, billed per " + plan.interval + ".",
      url: SITE_URL + "/pricing",
      price: price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      category: "subscription",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: price,
        priceCurrency: "USD",
        billingDuration: 1,
        billingIncrement: 1,
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: 1,
          unitCode: UNIT_CODE[plan.interval] || undefined,
        },
      },
    });
  }
  return offers.length ? offers : null;
}

/**
 * Service - an online peer-sponsor connection platform.
 * `plans` is the live response from GET /api/billing/plans. When it is not
 * available we simply omit offers rather than publish stale prices.
 */
function serviceSchema(plans) {
  const svc = config.service;
  const node = {
    "@type": "Service",
    "@id": SERVICE_ID,
    name: svc.name,
    serviceType: svc.serviceType,
    description: svc.description,
    url: SITE_URL + "/",
    provider: { "@id": ORG_ID },
    areaServed: { "@type": "Place", name: svc.areaServed },
    audience: { "@type": "Audience", audienceType: svc.audience },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: SITE_URL + "/sponsors",
      name: "Sponsor Me online sponsor directory",
    },
    termsOfService: SITE_URL + "/terms",
    disambiguatingDescription: config.organization.independenceDisclaimer,
  };
  const offers = offersFromPlans(plans);
  if (offers) {
    node.offers = offers;
  }
  return node;
}

/** FAQPage - built from the exact Q&A rendered by the homepage FAQ section. */
function faqSchema() {
  return {
    "@type": "FAQPage",
    "@id": SITE_URL + "/#faq",
    mainEntity: faqContent.homeSafetyFaqs.map(function (item) {
      return {
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a.join(" ") },
      };
    }),
  };
}

/** WebPage node describing the current route. */
function webPageSchema(page, path) {
  const url = canonicalFor(path);
  return {
    "@type": path === "/about" ? "AboutPage" : path === "/contact" ? "ContactPage" : "WebPage",
    "@id": url + "#webpage",
    url: url,
    name: page.title,
    description: page.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    inLanguage: "en-US",
    primaryImageOfPage: { "@type": "ImageObject", url: absolute(config.site.image) },
  };
}

/**
 * Build the single @graph for a route.
 * @param {object} page  metadata object ({title, description, index,...})
 * @param {string} path  route path, e.g. "/pricing"
 * @param {object} opts  { plans } live billing plans (optional)
 */
function graphForPath(page, path, opts) {
  const options = opts || {};
  if (page && page.index === false) return null; // never publish schema for noindex pages

  const graph = [organizationSchema(), websiteSchema(), webPageSchema(page, path)];

  if (path === "/") {
    graph.push(serviceSchema(options.plans));
    graph.push(faqSchema());
  } else if (path === "/pricing") {
    graph.push(serviceSchema(options.plans));
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

/**
 * Public sponsor profile pages.
 * Uses ONLY the same public, sponsor-chosen information that is already
 * rendered on the page (display name, program, availability, public intro).
 * No email, phone, exact address or other private data is ever included.
 */
function sponsorProfileGraph(meta) {
  if (!meta || meta.index === false) return null;
  const url = canonicalFor(meta.path);
  return {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema(),
      websiteSchema(),
      {
        "@type": "ProfilePage",
        "@id": url + "#webpage",
        url: url,
        name: meta.title,
        description: meta.description,
        isPartOf: { "@id": WEBSITE_ID },
        inLanguage: "en-US",
        primaryImageOfPage: { "@type": "ImageObject", url: absolute(config.site.image) },
      },
    ],
  };
}

/** Routes whose schema embeds live pricing. */
function needsPlans(path) {
  return path === "/" || path === "/pricing";
}

module.exports = {
  SCHEMA_SCRIPT_ID: "sm-structured-data",
  graphForPath: graphForPath,
  sponsorProfileGraph: sponsorProfileGraph,
  needsPlans: needsPlans,
  organizationSchema: organizationSchema,
  websiteSchema: websiteSchema,
  serviceSchema: serviceSchema,
  faqSchema: faqSchema,
};
